/**
 * ChicagoCupWatch.tsx — Public landing page for Carmel FC N1 G2012/13
 * Chicago Cup streaming hub.
 *
 * URL: /chicago
 * No login required.
 *
 * Same mechanics as USYSWatch / NLCupWatch (30s poll of /api/games/:id/public,
 * live badge, final score, inline replay). Light "Chicago Cup" treatment:
 * white ground, navy + red + sky blue, serif display face — matched to the
 * Chicago Cup logo rather than the dark USYS/NLCUP format.
 */
import React, { useEffect, useState } from 'react';
import { mediaUrl } from '../utils/mediaUrl';

// ── Chicago Cup brand colors (sampled from the tournament logo) ───────────────
const CHI_NAVY  = '#1F2A5A';   // trophy navy
const CHI_RED   = '#E1252B';   // "CHICAGO CUP" wordmark + flag stars
const CHI_SKY   = '#8FC7E8';   // skyline light blue
const CHI_INK   = '#12183A';   // body text
const CHI_MUTED = '#6B7590';   // secondary text
const CHI_LINE  = '#E2E7F0';   // hairlines / borders
const CHI_PAGE  = '#F5F8FC';   // page ground
const WHITE     = '#ffffff';

const SERIF = "'Playfair Display', Georgia, 'Times New Roman', serif";
const SANS  = "'Inter', system-ui, -apple-system, sans-serif";

// ── Logos ─────────────────────────────────────────────────────────────────────
const CARMEL_LOGO = '/api/videos/proxy/logos/logo_a9aa2911-a077-417f-abd1-e1c8a714e3b7.jpeg';

// ── Games ─────────────────────────────────────────────────────────────────────
// Times stored Eastern (Carmel home time); CT shown first since the tournament
// is played in Vernon Hills, Illinois.
const GAMES = [
  {
    id:          '9279c8d6-422a-4770-874c-ab4ccfad998a',
    label:       'Game 1',
    day:         'Saturday',
    date:        'Sept 5',
    time:        '12:10 PM CT / 1:10 PM ET',
    opponent:    'Central Illinois United GA',
    opponentSub: 'Illinois',
  },
  {
    id:          'fd34fdde-631c-4c83-a846-b8d2caf0a493',
    label:       'Game 2',
    day:         'Saturday',
    date:        'Sept 5',
    time:        '2:50 PM CT / 3:50 PM ET',
    opponent:    'Galaxy GA',
    opponentSub: 'Illinois',
  },
  {
    id:          '8b00b489-cb35-439d-8659-4eb4f5135303',
    label:       'Game 3',
    day:         'Sunday',
    date:        'Sept 6',
    time:        '4:00 PM CT / 5:00 PM ET',
    opponent:    'Chicago FC United GA',
    opponentSub: 'Illinois',
  },
];

interface GameState {
  status: string;
  home_score?: number;
  away_score?: number;
  recording?: {
    id: string;
    file_path: string;
    compressed_path?: string | null;
  } | null;
}

// ── Chicago flag star (six-pointed) ───────────────────────────────────────────
const starPoints = (cx: number, cy: number, outer: number, inner: number) => {
  const pts: string[] = [];
  for (let i = 0; i < 12; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / 6) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

// ── Chicago Cup emblem — hand-drawn vector recreation of the tournament mark ──
const ChicagoCupMark: React.FC<{ size?: number; showPresenter?: boolean }> = ({
  size = 210, showPresenter = true,
}) => (
  <svg width={size} height={size} viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg"
    role="img" aria-label="Chicago Cup">
    <circle cx="400" cy="400" r="396" fill={WHITE} stroke={CHI_LINE} strokeWidth="3" />

    {/* ── Soccer ball resting in the cup (drawn first; cup rim overlaps it) ── */}
    <g>
      <circle cx="135" cy="238" r="66" fill={WHITE} stroke={CHI_INK} strokeWidth="7" />
      <polygon points="135,205 160,223 150,252 120,252 110,223" fill={CHI_INK} />
      <path d="M135 172 L118 196 L152 196 Z" fill={CHI_INK} />
      <path d="M78 224 L100 210 L106 240 L84 250 Z" fill={CHI_INK} />
      <path d="M192 224 L170 210 L164 240 L186 250 Z" fill={CHI_INK} />
      <path d="M108 296 L118 268 L142 268 L152 296 Z" fill={CHI_INK} />
    </g>

    {/* ── Trophy ── */}
    <g fill={CHI_NAVY}>
      {/* handles */}
      <path d="M76 292 c-34 0 -50 18 -50 44 s16 44 50 44"
        fill="none" stroke={CHI_NAVY} strokeWidth="26" strokeLinecap="round" />
      <path d="M194 292 c38 0 56 18 56 44 s-18 44 -56 44"
        fill="none" stroke={CHI_NAVY} strokeWidth="26" strokeLinecap="round" />
      {/* bowl */}
      <path d="M62 276 h146 v92 c0 46 -33 80 -73 80 s-73 -34 -73 -80 z" />
      {/* stem */}
      <rect x="118" y="440" width="34" height="52" />
      {/* base */}
      <rect x="72" y="486" width="126" height="52" rx="8" />
    </g>
    {/* base highlight notch */}
    <rect x="104" y="500" width="62" height="20" rx="4" fill={WHITE} />

    {/* ── Chicago skyline ── */}
    <g fill={CHI_SKY}>
      {/* Willis Tower antennas */}
      <rect x="536" y="158" width="8" height="80" />
      <rect x="556" y="158" width="8" height="80" />
      <path d="M282 358 V318 H322 V292 H352 V330 H384 V282 H414 V316 H446 V266 H482 V300
               H520 V222 H580 V262 H612 V300 H644 V284 H676 V312 H708 V270 L730 246
               L752 270 V312 H770 V358 Z" />
    </g>

    {/* ── Wordmark ── */}
    <text x="272" y="470" fontFamily={SERIF} fontWeight="900" fontSize="100"
      textLength="488" lengthAdjust="spacingAndGlyphs"
      fill={CHI_RED}>CHICAGO</text>
    <text x="272" y="576" fontFamily={SERIF} fontWeight="900" fontSize="100"
      textLength="196" lengthAdjust="spacingAndGlyphs"
      fill={CHI_RED}>CUP</text>

    {/* four Chicago flag stars */}
    <g fill={CHI_RED}>
      {[505, 580, 655, 730].map(cx => (
        <polygon key={cx} points={starPoints(cx, 545, 28, 12.5)} />
      ))}
    </g>

    {showPresenter && (
      <text x="430" y="642" fontFamily={SANS} fontWeight="800" fontSize="26"
        textLength="280" lengthAdjust="spacingAndGlyphs"
        fill={CHI_INK}>PRESENTED BY ADIDAS</text>
    )}
  </svg>
);

// ── App Store badge ───────────────────────────────────────────────────────────
const AppStoreBadge: React.FC = () => (
  <a href="https://apps.apple.com/us/app/slow-rib-sports/id6760488944"
    target="_blank" rel="noopener noreferrer"
    style={{ display: 'inline-block', lineHeight: 0 }}>
    <svg width="140" height="42" viewBox="0 0 140 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="140" height="42" rx="7" fill="#000"/>
      <path d="M21.5 10.5c1.2-1.4 2-3.3 1.8-5.2-1.7.1-3.8 1.2-5 2.6-1.1 1.2-2 3.2-1.8 5 2 .2 3.9-.9 5-2.4z" fill="white"/>
      <path d="M23.3 13.3c-2.8-.2-5.1 1.6-6.4 1.6-1.3 0-3.3-1.5-5.4-1.5-2.8.1-5.4 1.7-6.8 4.2-2.9 5-.8 12.4 2 16.5 1.4 2 3 4.2 5.2 4.1 2-.1 2.8-1.3 5.2-1.3 2.4 0 3.1 1.3 5.2 1.3 2.2 0 3.7-2 5-4 1.6-2.3 2.2-4.5 2.3-4.6-.1 0-4.4-1.7-4.4-6.6 0-4.2 3.4-6.2 3.6-6.3-2-2.9-5-3.4-5.5-3.4z" fill="white"/>
      <text x="36" y="16" fill="white" fontFamily="-apple-system,Helvetica,Arial,sans-serif" fontSize="8" fontWeight="400" letterSpacing="0.2">Download on the</text>
      <text x="35" y="30" fill="white" fontFamily="-apple-system,Helvetica,Arial,sans-serif" fontSize="17" fontWeight="600" letterSpacing="-0.3">App Store</text>
    </svg>
  </a>
);

// ── Team logo with initials fallback ─────────────────────────────────────────
const TeamLogo: React.FC<{
  src: string | null;
  alt: string;
  size?: number;
  initials?: string;
}> = ({ src, alt, size = 58, initials = '?' }) => {
  const [errored, setErrored] = useState(false);
  return (
    <div style={{
      width: size, height: size, borderRadius: 12,
      background: WHITE, border: `1.5px solid ${CHI_LINE}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden', boxShadow: '0 1px 6px rgba(31,42,90,0.10)',
    }}>
      {src && !errored
        ? <img src={mediaUrl(src)} alt={alt}
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
            onError={() => setErrored(true)} />
        : <span style={{ fontFamily: SANS, fontSize: size * 0.26, fontWeight: 800, color: CHI_NAVY, opacity: 0.45, letterSpacing: '-0.5px' }}>
            {initials}
          </span>
      }
    </div>
  );
};

// ── Individual game card ──────────────────────────────────────────────────────
const GameCard: React.FC<{ game: typeof GAMES[number] }> = ({ game }) => {
  const [state, setState]           = useState<GameState | null>(null);
  const [loading, setLoading]       = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL ?? '/api';
    const fetch_ = () =>
      fetch(`${API_BASE}/games/${game.id}/public`)
        .then(r => r.ok ? r.json() : null)
        .then((data: any) => {
          if (!data) return;
          setState({
            status:     data.status,
            home_score: data.score?.home_score,
            away_score: data.score?.away_score,
            recording:  data.recording ?? null,
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    fetch_();
    const t = setInterval(fetch_, 30_000);
    return () => clearInterval(t);
  }, [game.id]);

  const isLive       = state?.status === 'live';
  const isCompleted  = state?.status === 'completed';
  const hasRecording = isCompleted && !!state?.recording?.file_path;

  const recordingUrl = hasRecording ? (() => {
    const HLS_BASE = process.env.REACT_APP_HLS_URL ?? '/hls';
    const fp = state!.recording!.compressed_path || state!.recording!.file_path;
    return (fp.startsWith('/api/') || fp.startsWith('http')) ? fp : `${HLS_BASE}${fp}`;
  })() : null;

  const scoreDisplay =
    isCompleted && state?.home_score !== undefined && state?.away_score !== undefined
      ? `${state.away_score}–${state.home_score}` : null;

  const oppInitials = game.opponent.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase();

  return (
    <div style={{
      background:   WHITE,
      border:       `1px solid ${isLive ? `${CHI_RED}66` : CHI_LINE}`,
      borderRadius: 16,
      overflow:     'hidden',
      boxShadow:    isLive
        ? `0 0 0 3px ${CHI_RED}1A, 0 6px 22px rgba(225,37,43,0.16)`
        : '0 2px 14px rgba(31,42,90,0.07)',
    }}>

      {/* Card header strip — navy with red accent edge */}
      <div style={{
        background:     CHI_NAVY,
        borderLeft:     `5px solid ${CHI_RED}`,
        padding:        '10px 16px',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="13" height="13" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
            <polygon points={starPoints(50, 50, 48, 21)} fill={CHI_RED} />
          </svg>
          <span style={{ fontFamily: SANS, fontSize: 11, fontWeight: 800, color: WHITE, letterSpacing: '1px', textTransform: 'uppercase' as const }}>
            {game.label}
          </span>
          {!loading && isLive && (
            <span style={{ fontFamily: SANS, fontSize: 9, fontWeight: 800, color: '#FFD7D9', background: 'rgba(225,37,43,0.35)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.8px' }}>
              🔴 LIVE
            </span>
          )}
          {!loading && isCompleted && (
            <span style={{ fontFamily: SANS, fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 20, padding: '2px 7px', letterSpacing: '0.5px' }}>
              FINAL{scoreDisplay ? ` · ${scoreDisplay}` : ''}
            </span>
          )}
        </div>
        <div style={{ textAlign: 'right', fontFamily: SANS }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: WHITE }}>{game.day}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>{game.date}</div>
        </div>
      </div>

      {/* Matchup */}
      <div style={{ padding: '18px 16px 14px', display: 'flex', alignItems: 'flex-start' }}>
        {/* Carmel FC */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
          <TeamLogo src={CARMEL_LOGO} alt="Carmel FC" size={58} initials="CFC" />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 800, color: CHI_INK, lineHeight: 1.2 }}>Carmel FC</div>
            <div style={{ fontFamily: SANS, fontSize: 10, color: CHI_MUTED, fontWeight: 600, marginTop: 1 }}>N1 G2012/13</div>
          </div>
        </div>

        {/* VS / score */}
        <div style={{ flexShrink: 0, width: 54, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, marginTop: 16 }}>
          {isCompleted && scoreDisplay
            ? <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 900, color: CHI_NAVY, letterSpacing: '-1px' }}>{scoreDisplay}</div>
            : <div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 900, color: CHI_RED, letterSpacing: '1px' }}>VS</div>
          }
        </div>

        {/* Opponent */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
          <TeamLogo src={null} alt={game.opponent} size={58} initials={oppInitials} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 800, color: CHI_INK, lineHeight: 1.2 }}>{game.opponent}</div>
            <div style={{ fontFamily: SANS, fontSize: 10, color: CHI_MUTED, fontWeight: 600, marginTop: 1 }}>{game.opponentSub}</div>
          </div>
        </div>
      </div>

      {/* Kickoff time (upcoming only) */}
      {!isCompleted && (
        <div style={{ textAlign: 'center', padding: '0 16px 4px' }}>
          <span style={{ fontFamily: SANS, fontSize: 11, color: CHI_MUTED, fontWeight: 600 }}>⏰ {game.time}</span>
        </div>
      )}

      {/* CTA */}
      <div style={{ padding: '12px 16px 16px', borderTop: `1px solid ${CHI_LINE}`, marginTop: 6, display: 'flex', gap: 10 }}>
        {!isCompleted && (
          <a href={`/live/${game.id}`} style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '12px 20px', borderRadius: 9, textDecoration: 'none',
            background: isLive ? CHI_RED : CHI_NAVY,
            color: WHITE, fontFamily: SANS, fontWeight: 800, fontSize: 14, letterSpacing: '-0.2px',
            whiteSpace: 'nowrap' as const,
            boxShadow: isLive ? '0 3px 14px rgba(225,37,43,0.35)' : '0 2px 8px rgba(31,42,90,0.2)',
          }}>
            {isLive
              ? <><div style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', animation: 'pulse 1.5s infinite' }} /> Watch Live</>
              : <><svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><polygon points="3,2 12,7 3,12"/></svg> Watch Stream</>
            }
          </a>
        )}
        {isCompleted && hasRecording && (
          <button onClick={() => setShowPlayer(p => !p)} style={{
            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '12px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', touchAction: 'manipulation',
            background: showPlayer ? CHI_MUTED : CHI_NAVY,
            color: WHITE, fontFamily: SANS, fontWeight: 800, fontSize: 14, letterSpacing: '-0.2px', whiteSpace: 'nowrap' as const,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white"><path d="M3 2.5l9 4.5-9 4.5V2.5z"/></svg>
            {showPlayer ? 'Hide Replay' : 'Watch Replay'}
          </button>
        )}
        {isCompleted && !hasRecording && (
          <span style={{ flex: 1, fontFamily: SANS, fontSize: 13, color: CHI_MUTED, fontStyle: 'italic', textAlign: 'center' }}>
            Replay uploading soon…
          </span>
        )}
      </div>

      {/* Inline video */}
      {showPlayer && recordingUrl && (
        <div style={{ padding: '0 14px 14px' }}>
          <video src={recordingUrl} controls playsInline autoPlay
            style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block', maxHeight: '70vw' }}
            onError={e => { const v = e.currentTarget; console.error('Video error:', v.error?.code, recordingUrl); }} />
          <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 11, color: CHI_MUTED, textAlign: 'center' }}>
            Full game recording · Carmel FC vs. {game.opponent}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Day section heading ───────────────────────────────────────────────────────
const DayHeading: React.FC<{ day: string; date: string }> = ({ day, date }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
    <svg width="12" height="12" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <polygon points={starPoints(50, 50, 48, 21)} fill={CHI_RED} />
    </svg>
    <h2 style={{ margin: 0, fontFamily: SANS, fontSize: 11, fontWeight: 800, color: CHI_NAVY, letterSpacing: '2px', textTransform: 'uppercase' as const }}>
      {day}
    </h2>
    <div style={{ flex: 1, height: 1, background: CHI_LINE }} />
    <span style={{ fontFamily: SANS, fontSize: 11, color: CHI_MUTED, fontWeight: 600 }}>{date}</span>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const ChicagoCupWatch: React.FC = () => {
  // Load the display serif only on this page rather than app-wide.
  useEffect(() => {
    const id = 'chicago-cup-serif';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id   = id;
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);

  const saturday = GAMES.filter(g => g.day === 'Saturday');
  const sunday   = GAMES.filter(g => g.day === 'Sunday');

  return (
    <div style={{
      minHeight: '100vh',
      background: CHI_PAGE,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: SANS,
    }}>

      {/* ── Hero header ── */}
      <div style={{
        width: '100%',
        background: WHITE,
        borderBottom: `4px solid ${CHI_RED}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 24px 30px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* faint skyline-blue wash behind the mark */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(ellipse at 50% 0%, ${CHI_SKY}26 0%, transparent 62%)`,
        }} />

        {/* The Chicago Cup mark */}
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <ChicagoCupMark size={230} />
        </div>

        {/* Four flag stars */}
        <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 14, position: 'relative' }}>
          {[0, 1, 2, 3].map(i => (
            <svg key={i} width="13" height="13" viewBox="0 0 100 100">
              <polygon points={starPoints(50, 50, 48, 21)} fill={CHI_RED} />
            </svg>
          ))}
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center', position: 'relative', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: CHI_MUTED, letterSpacing: '3px', textTransform: 'uppercase' as const, marginBottom: 8 }}>
            Live Streaming Hub
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 900, color: CHI_NAVY, letterSpacing: '-0.5px', lineHeight: 1.05 }}>
            Chicago Cup
          </div>
          <div style={{ fontSize: 13, color: CHI_MUTED, marginTop: 8, fontWeight: 500 }}>
            Vernon Hills, Illinois · September 5–6, 2026
          </div>
        </div>

        {/* Carmel FC identity chip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, position: 'relative',
          background: CHI_PAGE, border: `1px solid ${CHI_LINE}`,
          borderRadius: 50, padding: '8px 18px',
        }}>
          <img src={mediaUrl(CARMEL_LOGO)} alt="Carmel FC"
            style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: CHI_NAVY }}>
            Carmel FC N1 G2012/13
          </span>
        </div>
      </div>

      {/* ── Saturday ── */}
      <div style={{ width: '100%', maxWidth: 560, padding: '28px 20px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <DayHeading day="Saturday" date="September 5" />
        {saturday.map(g => <GameCard key={g.id} game={g} />)}
      </div>

      {/* ── Sunday ── */}
      <div style={{ width: '100%', maxWidth: 560, padding: '24px 20px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <DayHeading day="Sunday" date="September 6" />
        {sunday.map(g => <GameCard key={g.id} game={g} />)}
      </div>

      {/* ── Footer ── */}
      <div style={{ width: '100%', maxWidth: 560, padding: '30px 20px 44px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
        <div style={{ width: '100%', height: 1, background: CHI_LINE }} />

        <ChicagoCupMark size={92} showPresenter={false} />

        <p style={{ margin: 0, fontSize: 13, color: CHI_MUTED, textAlign: 'center' }}>
          Get the full experience — live scores, stats &amp; highlights
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', alignItems: 'center' }}>
          <AppStoreBadge />
          <a href="https://www.slowrib.com/register" target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 20px',
              background: WHITE, border: `1.5px solid ${CHI_RED}`,
              borderRadius: 8, color: CHI_RED, fontWeight: 700, fontSize: 14,
              textDecoration: 'none', whiteSpace: 'nowrap' as const,
            }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/>
            </svg>
            Create Free Account
          </a>
        </div>

        <div style={{ fontSize: 11, color: CHI_MUTED, opacity: 0.75, textAlign: 'center', lineHeight: 1.7 }}>
          Powered by{' '}
          <a href="https://www.slowrib.com" style={{ color: CHI_NAVY, textDecoration: 'none', fontWeight: 600 }}>Slow Rib Sports</a>
          {' · '}
          <a href="https://www.slowrib.com/privacy" style={{ color: CHI_MUTED, textDecoration: 'none' }}>Privacy</a>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      `}</style>
    </div>
  );
};

export default ChicagoCupWatch;
